import React from 'react';

/**
 * DeleteButton - Consistent delete button styling
 * 
 * Used for:
 * - Per-row delete actions
 * - Batch delete operations
 * 
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {string} props.title - Tooltip text
 * @param {string} props.size - 'small' | 'medium' | 'large'
 * @param {boolean} props.showLabel - Show text label
 * @param {string} props.label - Custom label text
 * @param {number} props.count - For batch delete, show count
 * @param {string} props.variant - 'icon' | 'button' | 'link'
 */
const DeleteButton = ({
  onClick,
  disabled = false,
  title = 'Delete',
  size = 'small',
  showLabel = false,
  label = 'Delete',
  count = null,
  variant = 'icon',
}) => {
  const sizes = {
    small: {
      padding: '4px 8px',
      fontSize: '0.75rem',
      iconSize: '0.9rem',
    },
    medium: {
      padding: '8px 16px',
      fontSize: '0.9rem',
      iconSize: '1rem',
    },
    large: {
      padding: '12px 20px',
      fontSize: '1rem',
      iconSize: '1.1rem',
    },
  };

  const sizeConfig = sizes[size] || sizes.small;

  // Icon-only variant (for per-row actions)
  if (variant === 'icon') {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        style={{
          padding: sizeConfig.padding,
          backgroundColor: disabled ? '#f8d7da' : '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: sizeConfig.iconSize,
          opacity: disabled ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.25rem',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = '#c82333';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = '#dc3545';
          }
        }}
      >
        🗑️
        {showLabel && <span>{label}</span>}
      </button>
    );
  }

  // Button variant (for batch delete)
  if (variant === 'button') {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        style={{
          padding: sizeConfig.padding,
          backgroundColor: disabled ? '#f8d7da' : '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: sizeConfig.fontSize,
          fontWeight: 'bold',
          opacity: disabled ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = '#c82333';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = '#dc3545';
          }
        }}
      >
        🗑️ {label}{count !== null && ` (${count})`}
      </button>
    );
  }

  // Link variant (for inline text actions)
  if (variant === 'link') {
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        title={title}
        style={{
          background: 'none',
          border: 'none',
          color: disabled ? '#e0a0a0' : '#dc3545',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: sizeConfig.fontSize,
          textDecoration: 'underline',
          padding: '0',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.color = '#c82333';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.color = '#dc3545';
          }
        }}
      >
        🗑️ {label}
      </button>
    );
  }

  return null;
};

/**
 * BatchDeleteButton - Styled batch delete button with count
 */
export const BatchDeleteButton = ({
  onClick,
  count = 0,
  disabled = false,
  label = 'Delete Selected',
}) => {
  if (count === 0) return null;

  return (
    <DeleteButton
      onClick={onClick}
      disabled={disabled}
      variant="button"
      size="medium"
      label={label}
      count={count}
      title={`Delete ${count} selected item(s)`}
    />
  );
};

/**
 * RowDeleteButton - Compact delete button for table rows
 */
export const RowDeleteButton = ({
  onClick,
  disabled = false,
  title = 'Delete this row',
}) => {
  return (
    <DeleteButton
      onClick={onClick}
      disabled={disabled}
      variant="icon"
      size="small"
      title={title}
    />
  );
};

export default DeleteButton;
